import ExcelJS from 'exceljs'
import { ProjectWithComments } from '../types/project'
import { Package } from '../enums/package.enum'
import { isMinimumPackage } from '../utils/package-validator'

interface IColumnType {
  [key: string]: boolean
  number: boolean
  stage: boolean
  name: boolean
  type: boolean
  price: boolean
  quantity: boolean
  unit: boolean
  value: boolean
  url: boolean
  image: boolean
  dimensions: boolean
  description: boolean
  status: boolean
  deliveryCost: boolean
  discountPercentage: boolean
  marginPercentage: boolean
  vatPercentage: boolean
  category: boolean
  supplier: boolean
  catalogNumber: boolean
}

const columnDisplayNames: { [key: string]: string } = {
  number: 'Lp.',
  stage: 'Etap',
  name: 'Nazwa',
  type: 'Typ',
  price: 'Cena (PLN)',
  quantity: 'Ilość',
  unit: 'Jednostka',
  value: 'Wartość (PLN)',
  url: 'URL',
  image: 'Zdjęcie',
  dimensions: 'Wymiary/Pojemność',
  description: 'Opis',
  status: 'Status',
  deliveryCost: 'Koszt dostawy',
  discountPercentage: 'Procent rabatu',
  marginPercentage: 'Procent marży',
  vatPercentage: 'Procent VAT',
  category: 'Kategoria',
  supplier: 'Dostawca',
  catalogNumber: 'Nr kat.'
}

export const useExcelExport = () => {
  const calculateItemValue = (item: any) => {
    const basePrice = item.price * item.quantity
    const withDelivery = basePrice + (item.deliveryCost || 0)
    const withMargin = withDelivery * (1 + ((item.marginPercentage || 0) / 100))
    const withVat = withMargin * (1 + ((item.vatPercentage || 0) / 100))
    const withDiscount = withVat * (1 - ((item.discountPercentage || 0) / 100))
    return withDiscount
  }

  const calculateTotalPrice = (items: any[]) => {
    if (items.length === 0) return 0
    return items.reduce((total, item) => total + calculateItemValue(item), 0)
  }

  const exportToExcel = async (
    project: ProjectWithComments,
    username: string | null,
    userPackage: string,
    packageExpiredAt: string | null,
    selectedExportColumns: IColumnType,
    onSuccess: () => void,
    onError: (message: string) => void
  ) => {
    if (!project) return

    // Uwaga dotycząca obrazów
    onSuccess()

    // Użytkownicy z pakietem START nie mają możliwości wyboru kolumn
    const isPlusOrPremium = isMinimumPackage(userPackage as Package, Package.PLUS, packageExpiredAt ? new Date(packageExpiredAt) : null)
    const columnsToExport = isPlusOrPremium
      ? selectedExportColumns
      : {
          number: true,
          stage: true,
          name: true,
          type: true,
          price: true,
          quantity: true,
          unit: true,
          value: true,
          url: true,
          description: true,
          status: true,
          deliveryCost: true,
          discountPercentage: true,
          marginPercentage: true,
          vatPercentage: true,
          image: true,
          dimensions: true,
          category: true,
          supplier: true,
          catalogNumber: true
        }

    // Tworzymy nowy skoroszyt Excel
    const workbook = new ExcelJS.Workbook()
    workbook.creator = username || 'Użytkownik'
    workbook.lastModifiedBy = username || 'Użytkownik'
    workbook.created = new Date()
    workbook.modified = new Date()

    // Tworzymy arkusz
    const worksheet = workbook.addWorksheet('Projekt')

    // Przygotowujemy nagłówki kolumn
    const headersKeys = []
    const headers = []
    if (columnsToExport.number) { headersKeys.push('number'); headers.push(columnDisplayNames.number) }
    if (columnsToExport.stage) { headersKeys.push('stage'); headers.push(columnDisplayNames.stage) }
    if (columnsToExport.name) { headersKeys.push('name'); headers.push(columnDisplayNames.name) }
    if (columnsToExport.type) { headersKeys.push('type'); headers.push(columnDisplayNames.type) }
    if (columnsToExport.price) { headersKeys.push('price'); headers.push(columnDisplayNames.price) }
    if (columnsToExport.quantity) { headersKeys.push('quantity'); headers.push(columnDisplayNames.quantity) }
    if (columnsToExport.unit) { headersKeys.push('unit'); headers.push(columnDisplayNames.unit) }
    if (columnsToExport.value) { headersKeys.push('value'); headers.push(columnDisplayNames.value) }
    if (columnsToExport.url) { headersKeys.push('url'); headers.push(columnDisplayNames.url) }
    if (columnsToExport.image) { headersKeys.push('image'); headers.push(columnDisplayNames.image) }
    if (columnsToExport.dimensions) { headersKeys.push('dimensions'); headers.push(columnDisplayNames.dimensions) }
    if (columnsToExport.description) { headersKeys.push('description'); headers.push(columnDisplayNames.description) }
    if (columnsToExport.status) { headersKeys.push('status'); headers.push(columnDisplayNames.status) }
    if (columnsToExport.category) { headersKeys.push('category'); headers.push(columnDisplayNames.category) }
    if (columnsToExport.deliveryCost) { headersKeys.push('deliveryCost'); headers.push(columnDisplayNames.deliveryCost) }
    if (columnsToExport.discountPercentage) { headersKeys.push('discountPercentage'); headers.push(columnDisplayNames.discountPercentage) }
    if (columnsToExport.marginPercentage) { headersKeys.push('marginPercentage'); headers.push(columnDisplayNames.marginPercentage) }
    if (columnsToExport.vatPercentage) { headersKeys.push('vatPercentage'); headers.push(columnDisplayNames.vatPercentage) }
    if (columnsToExport.supplier) { headersKeys.push('supplier'); headers.push(columnDisplayNames.supplier) }
    if (columnsToExport.catalogNumber) { headersKeys.push('catalogNumber'); headers.push(columnDisplayNames.catalogNumber) }
    worksheet.addRow(headers)

    // Stylizujemy nagłówki
    worksheet.getRow(1).font = { bold: true }
    worksheet.getRow(1).alignment = { vertical: 'middle', horizontal: 'center' }

    // Ustawiamy szerokości kolumn
    let colIndex = 1
    if (columnsToExport.number) worksheet.getColumn(colIndex++).width = 5
    if (columnsToExport.stage) worksheet.getColumn(colIndex++).width = 15
    if (columnsToExport.name) worksheet.getColumn(colIndex++).width = 30
    if (columnsToExport.type) worksheet.getColumn(colIndex++).width = 10
    if (columnsToExport.price) worksheet.getColumn(colIndex++).width = 12
    if (columnsToExport.quantity) worksheet.getColumn(colIndex++).width = 8
    if (columnsToExport.unit) worksheet.getColumn(colIndex++).width = 10
    if (columnsToExport.value) worksheet.getColumn(colIndex++).width = 15
    if (columnsToExport.url) worksheet.getColumn(colIndex++).width = 25
    if (columnsToExport.image) worksheet.getColumn(colIndex++).width = 25
    if (columnsToExport.dimensions) worksheet.getColumn(colIndex++).width = 20
    if (columnsToExport.description) worksheet.getColumn(colIndex++).width = 35
    if (columnsToExport.status) worksheet.getColumn(colIndex++).width = 15
    if (columnsToExport.category) worksheet.getColumn(colIndex++).width = 20
    if (columnsToExport.deliveryCost) worksheet.getColumn(colIndex++).width = 15
    if (columnsToExport.discountPercentage) worksheet.getColumn(colIndex++).width = 15
    if (columnsToExport.marginPercentage) worksheet.getColumn(colIndex++).width = 15
    if (columnsToExport.vatPercentage) worksheet.getColumn(colIndex++).width = 15
    if (columnsToExport.supplier) worksheet.getColumn(colIndex++).width = 20
    if (columnsToExport.catalogNumber) worksheet.getColumn(colIndex++).width = 15

    // Dodajemy dane
    for (let i = 0; i < project.items.length; i++) {
      const item = project.items[i]
      const stage = project.stages.find(s => s.id === item.stageId)

      // Tworzymy pełny wiersz zgodnie z headersKeys
      const rowData = headersKeys.map(key => {
        switch (key) {
          case 'number':
            return i + 1
          case 'stage':
            return (stage != null) ? stage.name : ''
          case 'name':
            return item.name
          case 'type':
            return item.type === 'product'
              ? 'Produkt'
              : item.type === 'service'
                ? 'Usługa'
                : item.type === 'note'
                  ? 'Notatka'
                  : item.type === 'visualization'
                    ? 'Wizualizacja'
                    : item.type === 'attachment' ? 'Załącznik' : ''
          case 'price':
            return Number(item.price.toFixed(2))
          case 'quantity':
            return item.quantity
          case 'unit':
            return item.unit
          case 'value':
            return Number(calculateItemValue(item).toFixed(2))
          case 'url':
            return item.url || ''
          case 'image':
            return '' // Placeholder, obrazek wstawiamy osobno
          case 'dimensions':
            return item.dimensions || ''
          case 'description':
            return item.description || ''
          case 'status':
            return item.status === 'approved'
              ? 'Zaakceptowany'
              : item.status === 'rejected' ? 'Odrzucony' : 'Oczekujący'
          case 'category':
            return item.category || ''
          case 'deliveryCost':
            return item.deliveryCost !== undefined ? Number(item.deliveryCost) : ''
          case 'discountPercentage':
            return item.discountPercentage !== undefined ? Number(item.discountPercentage) : ''
          case 'marginPercentage':
            return item.marginPercentage !== undefined ? Number(item.marginPercentage) : ''
          case 'vatPercentage':
            return item.vatPercentage !== undefined ? Number(item.vatPercentage) : ''
          case 'supplier':
            return item.supplier || ''
          case 'catalogNumber':
            return item.catalogNumber || ''
          default:
            return ''
        }
      })
      const addedRow = worksheet.addRow(rowData)

      // Dodaj obrazek jeśli jest imageUrl
      if (columnsToExport.image && item.imageUrl) {
        try {
          const imageColumnIndex = headersKeys.indexOf('image') + 1
          const rowIndex = addedRow.number // numer wiersza w arkuszu
          worksheet.getCell(rowIndex, imageColumnIndex).value = ''
          const response = await fetch(item.imageUrl)
          if (response.ok) {
            const imageArrayBuffer = await response.arrayBuffer()
            let extension = item.imageUrl.split('.').pop()?.toLowerCase() || 'png'
            if (!['png', 'jpeg', 'gif'].includes(extension)) {
              extension = 'png'
            }
            const imageId = workbook.addImage({
              buffer: new Uint8Array(imageArrayBuffer) as any,
              extension: extension as 'png' | 'jpeg' | 'gif'
            })
            worksheet.addImage(imageId, {
              tl: { col: imageColumnIndex - 1, row: rowIndex - 1 },
              ext: { width: 100, height: 80 },
              editAs: 'oneCell'
            })
            worksheet.getRow(rowIndex).height = 60
          } else {
            worksheet.getCell(rowIndex, imageColumnIndex).value = '[Nie udało się pobrać obrazu]'
          }
        } catch (error) {
          const imageColumnIndex = headersKeys.indexOf('image') + 1
          worksheet.getCell(addedRow.number, imageColumnIndex).value = '[Błąd podczas ładowania obrazu]'
        }
      }
    }

    // Dodajemy wiersz podsumowujący
    const totalValue = calculateTotalPrice(project.items)
    const summaryRowData: any[] = []
    headersKeys.forEach(key => {
      switch (key) {
        case 'name':
          summaryRowData.push('RAZEM')
          break
        case 'value':
          summaryRowData.push(Number(totalValue.toFixed(2)))
          break
        default:
          summaryRowData.push('')
      }
    })

    const summaryRow = worksheet.addRow(summaryRowData)
    summaryRow.font = { bold: true }

    try {
      // Zapisujemy plik Excel
      const buffer = await workbook.xlsx.writeBuffer()
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      const url = window.URL.createObjectURL(blob)

      // Tworzymy link do pobrania i klikamy go
      const a = document.createElement('a')
      a.href = url
      a.download = `${project.name} - wycena.xlsx`
      a.click()

      // Zwalniamy URL
      window.URL.revokeObjectURL(url)

      onSuccess()
    } catch (error) {
      console.error('Błąd podczas generowania pliku Excel:', error)
      onError('Nie udało się wygenerować pliku Excel')
    }
  }

  return {
    exportToExcel
  }
}
